import React, { useState, useRef, useEffect } from 'react';
import Navblock from './Navblock';
import AddingMedication from './Adding-medication';
import CsvDataPopup from './csvdata-popup';
import SavedPurchased from './saved-purchased';
import AddDistributor from './Add-distrubutor';
import EditDistributor from './edit-distributor-details';
import { useNavigation } from '../navigation';

const PurchasePage: React.FC = () => {
  const { navigateTo } = useNavigation();
  const [showCsvPopup, setShowCsvPopup] = useState(false);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvSourceType, setCsvSourceType] = useState<'csv' | 'pdf'>('csv');
  const [notification, setNotification] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });
  const [showSavedPurchases, setShowSavedPurchases] = useState(true);
  const [showAddDistributor, setShowAddDistributor] = useState(false);
  const [showEditDistributor, setShowEditDistributor] = useState(false);
  const [editingDistributor, setEditingDistributor] = useState<any | null>(null);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [imagePopupData, setImagePopupData] = useState<{ url: string; name: string } | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSaveConfirmPopup, setShowSaveConfirmPopup] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [initialPurchaseData, setInitialPurchaseData] = useState<{ rows: any[]; selectedDistributor: any | null } | null>(null);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [hasUsedUpload, setHasUsedUpload] = useState(false);
  const isNavigatingRef = useRef(false);
  const isPopupShowingRef = useRef(false);
  const navigationStepsRef = useRef(0); // Track how many navigation steps to ignore
  const popStateHandlerRef = useRef<((e: PopStateEvent) => void) | null>(null);
  const applyCsvMappingRef = useRef<((csvData: any[], mapping: { [key: string]: string | string[] }) => void) | null>(null);
  const refreshDistributorsRef = useRef<(() => void) | null>(null);
  const refreshSavedPurchasesRef = useRef<(() => void) | null>(null);
  const resetFormRef = useRef<(() => void) | null>(null);
  const getPurchaseDataRef = useRef<(() => { rows: any[]; selectedDistributor: any | null }) | null>(null);
  const doesRowContainData = (row: any) => (
    row['Medicine Name']?.trim() !== '' ||
    row['Brand Name']?.trim() !== '' ||
    row['Composition Name']?.trim() !== '' ||
    row['Batch Number']?.trim() !== '' ||
    row['Quantity'] > 0 ||
    row['Cost Price'] > 0 ||
    row['MRP'] > 0 ||
    row['Expiry Date']?.trim() !== ''
  );
  const hasUnsavedRows = (rows: any[]) => rows.some(doesRowContainData);
  const getCurrentPurchaseData = () => (getPurchaseDataRef.current ? getPurchaseDataRef.current() : null);
  const hasUnsavedChanges = () => {
    const data = getCurrentPurchaseData();
    if (!data) {
      return false;
    }
    return hasUnsavedRows(data.rows);
  };
  const promptToSaveBeforeNavigation = (navFn: () => void) => {
    if (hasUnsavedChanges()) {
      if (isPopupShowingRef.current) {
        return;
      }
      isPopupShowingRef.current = true;
      setPendingNavigation(() => navFn);
      setShowSaveConfirmPopup(true);
    } else {
      navFn();
    }
  };
  const executePendingNavigation = () => {
    const navFn = pendingNavigation;
    setPendingNavigation(null);
    if (navFn) {
      navFn();
    }
  };

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load any saved purchase data from localStorage so the table can hydrate automatically
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const savedData = localStorage.getItem('Purchase-data');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData && Array.isArray(parsedData.rows)) {
          setInitialPurchaseData(parsedData);
        }
      }
    } catch (error) {
      console.error('Failed to load saved purchase data from localStorage:', error);
    }
  }, []);

  const handleCsvUpload = (data: any[], sourceType?: 'csv' | 'pdf') => {
    setCsvData(data);
    setCsvSourceType(sourceType || 'csv');
    setShowCsvPopup(true);
  };

  const handleApplyMapping = (mapping: { [key: string]: string | string[] }) => {
    
    // Apply mapping to PurchaseTable rows
    if (applyCsvMappingRef.current && csvData.length > 0) {
      applyCsvMappingRef.current(csvData, mapping);
    }
  };

  const handleRegisterCsvMapping = (applyFn: (csvData: any[], mapping: { [key: string]: string | string[] }) => void) => {
    applyCsvMappingRef.current = applyFn;
  };

  const handleSubmitSuccess = (message: string) => {
    setNotification({
      show: true,
      message: message,
      type: 'success'
    });
    
    // Refresh saved purchases to show the newly submitted purchase
    if (refreshSavedPurchasesRef.current) {
      refreshSavedPurchasesRef.current();
    }
    
    // Reset the form after a short delay to allow the notification to show
    setTimeout(() => {
      if (resetFormRef.current) {
        resetFormRef.current();
      }
    }, 500);
  };

  const handleSubmitError = (message: string) => {
    setNotification({
      show: true,
      message: message,
      type: 'error'
    });
  };

  const handleSaveConfirmYes = () => {
    if (getPurchaseDataRef.current) {
      const data = getPurchaseDataRef.current();
      localStorage.setItem('Purchase-data', JSON.stringify(data));
      setNotification({
        show: true,
        message: 'Purchase data saved successfully!',
        type: 'success'
      });
    }
    isPopupShowingRef.current = false;
    setShowSaveConfirmPopup(false);
    executePendingNavigation();
  };

  const handleSaveConfirmNo = () => {
    isPopupShowingRef.current = false;
    setShowSaveConfirmPopup(false);
    executePendingNavigation();
  };

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  // Handle browser back button and page unload
  useEffect(() => {
    // Add a history entry to intercept back button
    window.history.pushState({ page: 'purchase' }, '', window.location.href);

    const handlePopState = (e: PopStateEvent) => {
      // Don't show popup if we're already navigating
      if (isNavigatingRef.current) {
        navigationStepsRef.current--;
        if (navigationStepsRef.current <= 0) {
          isNavigatingRef.current = false;
          navigationStepsRef.current = 0;
        }
        return;
      }

      // Don't show popup if it's already showing
      if (isPopupShowingRef.current) {
        return;
      }

      const data = getCurrentPurchaseData();

      if (data && hasUnsavedRows(data.rows)) {
        // Push state again to prevent immediate navigation
        window.history.pushState({ page: 'purchase' }, '', window.location.href);
        
        // Store navigation function and show popup
        isPopupShowingRef.current = true;
        setPendingNavigation(() => {
          return () => {
            isNavigatingRef.current = true;
            navigationStepsRef.current = 2;
            if (popStateHandlerRef.current) {
              window.removeEventListener('popstate', popStateHandlerRef.current);
            }
            if (document.referrer && document.referrer !== window.location.href) {
              window.location.href = document.referrer;
            } else {
              window.history.go(-2);
            }
          };
        });
        setShowSaveConfirmPopup(true);
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const data = getCurrentPurchaseData();

      if (data && hasUnsavedRows(data.rows)) {
          // Silently save data without triggering browser dialog
          localStorage.setItem('Purchase-data', JSON.stringify(data));
      }
    };

    // Store handler reference for later removal
    popStateHandlerRef.current = handlePopState;
    
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <Navblock 
        onViewSavedPurchases={() => setShowSavedPurchases(!showSavedPurchases)}
        isSavedPurchasesVisible={showSavedPurchases}
        onNavigateHomeRequest={() => promptToSaveBeforeNavigation(() => navigateTo('pharma-homepage'))}
      />

      {/* Upload loading overlay - shown from Purchase-page while document is uploading */}
      {isUploadingDocument && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '2rem 1.5rem' : '3rem 2.5rem',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
            maxWidth: isMobile ? '90%' : '400px',
            width: '100%',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{
              width: isMobile ? '60px' : '80px',
              height: isMobile ? '60px' : '80px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <div style={{ textAlign: 'center' }}>
              <h3 style={{
                margin: '0 0 0.5rem 0',
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                Uploading Document
              </h3>
              <p style={{
                margin: 0,
                fontSize: isMobile ? '0.875rem' : '0.9375rem',
                color: '#6b7280',
                lineHeight: '1.5'
              }}>
                Please wait while we process your file...
              </p>
              <p style={{
                margin: '1rem 0 0 0',
                padding: '0.75rem 1rem',
                fontSize: isMobile ? '0.8125rem' : '0.875rem',
                color: '#92400e',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #f59e0b',
                lineHeight: '1.5',
                fontWeight: '600'
              }}>
                AI can make mistakes. Please review the data before clicking Submit.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Banner above table: upload CTA when upload not used, AI disclaimer when upload was used */}
      {hasUsedUpload ? (
        <div
          className="purchase-page-ai-banner"
          style={{
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: 0,
            overflow: 'hidden',
            background: 'linear-gradient(90deg, #fffbeb 0%, #fef3c7 20%, #fde68a 50%, #fef3c7 80%, #fffbeb 100%)',
            borderTop: '1px solid rgba(245, 158, 11, 0.25)',
            borderBottom: '1px solid rgba(245, 158, 11, 0.25)',
            boxShadow: '0 2px 12px rgba(245, 158, 11, 0.08)',
            position: 'relative'
          }}
        >
          <div
            className="purchase-page-marquee-track"
            style={{
              display: 'flex',
              width: 'max-content',
              animation: 'purchase-page-marquee 32s linear infinite',
              padding: isMobile ? '0.5rem 0' : '0.65rem 0',
              gap: '3rem',
              alignItems: 'center',
              willChange: 'transform'
            }}
          >
            {[...Array(10)].map((_, i) => (
              <span
                key={i}
                style={{
                  fontSize: isMobile ? '0.8125rem' : '0.875rem',
                  fontWeight: '600',
                  color: '#92400e',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexShrink: 0,
                  letterSpacing: '0.01em'
                }}
              >
                <span style={{ opacity: 0.85, fontSize: '1em' }} aria-hidden>⚠</span>
                AI can make mistakes. Please review the data before clicking Submit.
              </span>
            ))}
          </div>
          <style>{`
            @keyframes purchase-page-marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .purchase-page-marquee-track:hover {
              animation-play-state: paused;
            }
          `}</style>
        </div>
      ) : (
        <div
          style={{
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: isMobile ? '0.5rem 1rem' : '0.65rem 1.5rem',
            textAlign: 'center',
            background: 'linear-gradient(90deg, #eff6ff 0%, #dbeafe 50%, #eff6ff 100%)',
            borderTop: '1px solid rgba(59, 130, 246, 0.2)',
            borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.06)'
          }}
        >
          <span
            style={{
              fontSize: isMobile ? '0.8125rem' : '0.875rem',
              fontWeight: '600',
              color: '#1e40af',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              letterSpacing: '0.01em'
            }}
          >
            <span aria-hidden>📤</span>
            Use the upload option to auto-fill the fields using an image or CSV file..
          </span>
        </div>
      )}
      
      {/* Adding Medication - Full Width */}
      <AddingMedication 
        onCsvUpload={handleCsvUpload}
        onRegisterCsvMapping={handleRegisterCsvMapping}
        onUploadStart={() => setIsUploadingDocument(true)}
        onUploadEnd={() => setIsUploadingDocument(false)}
        onUploadUsed={() => setHasUsedUpload(true)}
        onSubmitSuccess={handleSubmitSuccess}
        onSubmitError={handleSubmitError}
        onAddDistributor={() => setShowAddDistributor(true)}
        onRegisterDistributorRefresh={(refreshFn: () => void) => {
          refreshDistributorsRef.current = refreshFn;
        }}
        onEditDistributor={(distributor) => {
          setEditingDistributor(distributor);
          setShowEditDistributor(true);
        }}
        onRegisterFormReset={(resetFn: () => void) => {
          resetFormRef.current = resetFn;
        }}
        onRegisterGetData={(getDataFn: () => { rows: any[]; selectedDistributor: any | null }) => {
          getPurchaseDataRef.current = getDataFn;
        }}
        initialPurchaseData={initialPurchaseData || undefined}
        onImageClick={(imageUrl: string, medicineName: string) => {
          setImagePopupData({ url: imageUrl, name: medicineName });
          setShowImagePopup(true);
        }}
      />

      {/* Saved Purchases - Below Purchase Table */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '1rem 0.5rem' : '2rem',
        paddingTop: isMobile ? '1rem' : '1.5rem'
      }}>
        <SavedPurchased 
          isOpen={showSavedPurchases}
          onClose={() => setShowSavedPurchases(false)}
          onConfirmSuccess={(message: string) => {
            setNotification({
              show: true,
              message: message,
              type: 'success'
            });
          }}
          onConfirmError={(message: string) => {
            setNotification({
              show: true,
              message: message,
              type: 'error'
            });
          }}
          onRegisterRefresh={(refreshFn: () => void) => {
            refreshSavedPurchasesRef.current = refreshFn;
          }}
        />
      </div>
      
      {/* CSV Data Popup */}
      <CsvDataPopup
        isOpen={showCsvPopup}
        onClose={() => {
          setShowCsvPopup(false);
          setCsvData([]);
        }}
        csvData={csvData}
        onApplyMapping={handleApplyMapping}
        sourceType={csvSourceType}
      />

      {/* Add Distributor Modal */}
      <AddDistributor
        isOpen={showAddDistributor}
        onClose={() => setShowAddDistributor(false)}
        onSuccess={(message: string) => {
          setNotification({
            show: true,
            message: message,
            type: 'success'
          });
        }}
        onError={(message: string) => {
          setNotification({
            show: true,
            message: message,
            type: 'error'
          });
        }}
        onDistributorAdded={() => {
          // Refresh distributor list in PurchaseTable
          if (refreshDistributorsRef.current) {
            refreshDistributorsRef.current();
          }
        }}
      />

      {/* Edit Distributor Modal */}
      <EditDistributor
        isOpen={showEditDistributor}
        distributor={editingDistributor}
        onClose={() => {
          setShowEditDistributor(false);
          setEditingDistributor(null);
        }}
        onSuccess={(message: string) => {
          setNotification({
            show: true,
            message: message,
            type: 'success'
          });
        }}
        onError={(message: string) => {
          setNotification({
            show: true,
            message: message,
            type: 'error'
          });
        }}
        onDistributorUpdated={() => {
          // Refresh distributor list in PurchaseTable
          if (refreshDistributorsRef.current) {
            refreshDistributorsRef.current();
          }
        }}
      />
      
      {/* Main Content Area */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '1rem 0.5rem' : '3rem 2rem'
      }}>
        {/* Other content can go here */}
      </div>

      {/* Image Popup Modal */}
      {showImagePopup && imagePopupData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '1rem' : '2rem',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => {
            setShowImagePopup(false);
            setImagePopupData(null);
          }}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: 'white',
              borderRadius: '12px',
              padding: isMobile ? '1rem' : '1.5rem',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setShowImagePopup(false);
                setImagePopupData(null);
              }}
              style={{
                position: 'absolute',
                top: isMobile ? '0.5rem' : '1rem',
                right: isMobile ? '0.5rem' : '1rem',
                width: '32px',
                height: '32px',
                background: 'rgba(0, 0, 0, 0.5)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            {/* Medicine Name */}
            <div style={{
              fontSize: isMobile ? '1rem' : '1.25rem',
              fontWeight: '600',
              color: '#1f2937',
              textAlign: 'center',
              marginTop: '0.5rem'
            }}>
              {imagePopupData.name}
            </div>

            {/* Image */}
            <div style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f3f4f6',
              borderRadius: '8px',
              overflow: 'hidden',
              maxHeight: isMobile ? '60vh' : '70vh'
            }}>
              <img
                src={imagePopupData.url}
                alt={imagePopupData.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: isMobile ? '60vh' : '70vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = '<div style="padding: 2rem; text-align: center; color: #6b7280;">Image not available</div>';
                  }
                }}
              />
            </div>
          </div>
          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}

      {/* Save Confirmation Popup */}
      {showSaveConfirmPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '1rem' : '2rem',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={handleSaveConfirmNo}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: isMobile ? '1.5rem' : '2rem',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              animation: 'slideUp 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              flexShrink: 0
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
            </div>

            {/* Title */}
            <h2 style={{
              margin: '0 0 0.75rem 0',
              fontSize: isMobile ? '1.125rem' : '1.25rem',
              fontWeight: '700',
              color: '#1f2937',
              textAlign: 'center'
            }}>
              Save Purchase Data?
            </h2>

            {/* Message */}
            <p style={{
              margin: '0 0 1.5rem 0',
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              color: '#6b7280',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              Do you want to save the data before going back?
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleSaveConfirmNo}
                style={{
                  background: 'white',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  padding: isMobile ? '0.625rem 1.25rem' : '0.75rem 1.5rem',
                  fontSize: isMobile ? '0.875rem' : '0.9375rem',
                  fontWeight: '600',
                  color: '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flex: 1,
                  maxWidth: '150px'
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.borderColor = '#9ca3af';
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                No
              </button>
              <button
                onClick={handleSaveConfirmYes}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: isMobile ? '0.625rem 1.25rem' : '0.75rem 1.5rem',
                  fontSize: isMobile ? '0.875rem' : '0.9375rem',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flex: 1,
                  maxWidth: '150px',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                  }
                }}
              >
                Yes, Save
              </button>
            </div>
          </div>
          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            @keyframes slideUp {
              from {
                transform: translateY(20px);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? '10px' : '20px',
            right: isMobile ? '10px' : '20px',
            left: isMobile ? '10px' : 'auto',
            zIndex: 10000,
            background: notification.type === 'success' 
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            padding: isMobile ? '0.875rem 1rem' : '1rem 1.5rem',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '0.75rem',
            minWidth: isMobile ? 'auto' : '300px',
            maxWidth: isMobile ? '100%' : '500px',
            width: isMobile ? 'calc(100% - 20px)' : 'auto',
            animation: 'slideInRight 0.3s ease-out',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
          onClick={() => setNotification(prev => ({ ...prev, show: false }))}
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {notification.type === 'success' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            )}
          </div>
          <div style={{ 
            flex: 1, 
            fontSize: isMobile ? '0.875rem' : '0.9375rem', 
            fontWeight: '500',
            lineHeight: '1.4'
          }}>
            {notification.message}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setNotification(prev => ({ ...prev, show: false }));
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '6px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default PurchasePage;

